class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => {
        if (evt.code === 'Escape') {
          this.close();
        }
      }
    );

    const overlay =
      this.querySelector(
        '#CartDrawer-Overlay'
      );

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    this.setHeaderCartIconAccessibility();

    /*
     * =========================================
     * iOS CART DRAWER TOUCH FIX
     * =========================================
     */

    this.setupIOSTouchHandlers();
  }

  /*
   * =========================================
   * iOS TOUCH HANDLERS
   * =========================================
   */

  setupIOSTouchHandlers() {
    const isIOS =
      /iPad|iPhone|iPod/.test(
        navigator.userAgent
      ) ||
      (
        navigator.platform === 'MacIntel' &&
        navigator.maxTouchPoints > 1
      );

    if (!isIOS) return;

    let touchHandled = false;

    this.addEventListener(
      'touchstart',
      () => {
        touchHandled = false;
      },
      {
        passive: true
      }
    );

    this.addEventListener(
      'touchend',
      (event) => {

        const target =
          event.target;

        /*
         * -----------------------------------------
         * CLOSE DRAWER
         * -----------------------------------------
         */

        const closeButton =
          target.closest(
            '.drawer__close, .cart-drawer__close, [data-close-drawer]'
          );

        if (closeButton) {

          event.preventDefault();

          touchHandled = true;

          this.close();

          return;
        }

        /*
         * -----------------------------------------
         * QUANTITY PLUS / MINUS
         * -----------------------------------------
         */

        const quantityButton =
          target.closest(
            '.quantity__button'
          );

        if (quantityButton) {

          event.preventDefault();

          touchHandled = true;

          const quantityWrapper =
            quantityButton.closest(
              'quantity-input'
            );

          if (!quantityWrapper) {
            return;
          }

          const input =
            quantityWrapper.querySelector(
              '.quantity__input'
            );

          if (!input) {
            return;
          }

          const currentValue =
            parseInt(
              input.value,
              10
            ) || 0;

          const min =
            parseInt(
              input.getAttribute('min'),
              10
            );

          const maxAttribute =
            input.getAttribute('max');

          const max =
            maxAttribute
              ? parseInt(
                  maxAttribute,
                  10
                )
              : Infinity;

          let newValue =
            currentValue;

          if (
            quantityButton.name ===
            'plus'
          ) {

            newValue =
              Math.min(
                currentValue + 1,
                max
              );

          } else if (
            quantityButton.name ===
            'minus'
          ) {

            newValue =
              Math.max(
                currentValue - 1,
                Number.isNaN(min)
                  ? 0
                  : min
              );

          }

          input.value =
            newValue;

          /*
           * Trigger CartItems change handler
           */

          input.dispatchEvent(
            new Event(
              'change',
              {
                bubbles: true
              }
            )
          );

          return;
        }

        /*
         * -----------------------------------------
         * REMOVE ITEM
         * -----------------------------------------
         */

        const removeButton =
          target.closest(
            'cart-remove-button'
          );

        if (removeButton) {

          event.preventDefault();

          touchHandled = true;

          const cartItems =
            removeButton.closest(
              'cart-items'
            ) ||
            removeButton.closest(
              'cart-drawer-items'
            );

          if (
            cartItems &&
            typeof cartItems.updateQuantity ===
              'function'
          ) {

            cartItems.updateQuantity(
              removeButton.dataset.index,
              0
            );
          }

          return;
        }

        /*
         * -----------------------------------------
         * CHECKOUT BUTTON
         * -----------------------------------------
         */

        const checkoutButton =
          target.closest(
            '#CartDrawer-Checkout'
          );

        if (checkoutButton) {

          event.preventDefault();

          touchHandled = true;

          const formId =
            checkoutButton.getAttribute(
              'form'
            );

          const form =
            formId
              ? document.getElementById(
                  formId
                )
              : checkoutButton.closest(
                  'form'
                );

          if (form) {

            if (
              typeof form.requestSubmit ===
              'function'
            ) {

              form.requestSubmit(
                checkoutButton
              );

            } else {

              const checkoutInput =
                document.createElement(
                  'input'
                );

              checkoutInput.type =
                'hidden';

              checkoutInput.name =
                'checkout';

              form.appendChild(
                checkoutInput
              );

              form.submit();
            }
          }

          return;
        }

        /*
         * -----------------------------------------
         * VIEW CART
         * -----------------------------------------
         */

        const viewCartButton =
          target.closest(
            '#CartDrawer-Cart'
          );

        if (viewCartButton) {

          event.preventDefault();

          touchHandled = true;

          const href =
            viewCartButton.getAttribute(
              'href'
            );

          if (href) {
            window.location.href =
              href;
          }

          return;
        }

        /*
         * -----------------------------------------
         * GIFT WRAP CHECKBOX
         * -----------------------------------------
         */

        const checkbox =
          target.closest(
            'input[type="checkbox"]'
          );

        if (checkbox) {

          event.preventDefault();

          touchHandled = true;

          checkbox.checked =
            !checkbox.checked;

          checkbox.dispatchEvent(
            new Event(
              'change',
              {
                bubbles: true
              }
            )
          );

          return;
        }

      },
      {
        passive: false
      }
    );

    /*
     * Prevent accidental duplicate native click
     * immediately after our touch action.
     */

    this.addEventListener(
      'click',
      (event) => {

        if (touchHandled) {

          event.preventDefault();
          event.stopPropagation();

          touchHandled = false;
        }

      },
      true
    );
  }

  /*
   * =========================================
   * HEADER CART ICON
   * =========================================
   */

  setHeaderCartIconAccessibility() {

    const cartLink =
      document.querySelector(
        '#cart-icon-bubble'
      );

    if (!cartLink) return;

    cartLink.setAttribute(
      'role',
      'button'
    );

    cartLink.setAttribute(
      'aria-haspopup',
      'dialog'
    );

    cartLink.addEventListener(
      'click',
      (event) => {

        event.preventDefault();

        this.open(
          cartLink
        );

      }
    );

    cartLink.addEventListener(
      'keydown',
      (event) => {

        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {

          event.preventDefault();

          this.open(
            cartLink
          );
        }

      }
    );
  }

  /*
   * =========================================
   * OPEN
   * =========================================
   */

  open(triggeredBy) {

    if (triggeredBy) {
      this.setActiveElement(
        triggeredBy
      );
    }

    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary'
      );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute(
        'role'
      )
    ) {

      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }

    setTimeout(() => {

      this.classList.add(
        'animate',
        'active'
      );

    });

    this.addEventListener(
      'transitionend',
      () => {

        const containerToTrapFocusOn =
          this.classList.contains(
            'is-empty'
          )
            ? this.querySelector(
                '.drawer__inner-empty'
              )
            : document.getElementById(
                'CartDrawer'
              );

        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );

        if (
          containerToTrapFocusOn &&
          typeof trapFocus ===
            'function'
        ) {

          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }

      },
      {
        once: true
      }
    );

    document.body.classList.add(
      'overflow-hidden'
    );

    giftCart();
  }

  /*
   * =========================================
   * CLOSE
   * =========================================
   */

  close() {

    this.classList.remove(
      'active'
    );

    if (
      typeof removeTrapFocus ===
      'function'
    ) {

      removeTrapFocus(
        this.activeElement
      );
    }

    document.body.classList.remove(
      'overflow-hidden'
    );

    giftCart();
  }

  /*
   * =========================================
   * SUMMARY ACCESSIBILITY
   * =========================================
   */

  setSummaryAccessibility(
    cartDrawerNote
  ) {

    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute(
        'id'
      )
    ) {

      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote
          .nextElementSibling
          .id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {

        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget
            .closest('details')
            .hasAttribute('open')
        );

      }
    );

    if (
      cartDrawerNote.parentElement
    ) {

      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  /*
   * =========================================
   * RENDER CONTENTS
   * =========================================
   */

  renderContents(
    parsedState
  ) {

    const drawerInner =
      this.querySelector(
        '.drawer__inner'
      );

    if (
      drawerInner &&
      drawerInner.classList.contains(
        'is-empty'
      )
    ) {

      drawerInner.classList.remove(
        'is-empty'
      );
    }

    this.productId =
      parsedState.id;

    this.getSectionsToRender()
      .forEach(
        (section) => {

          const sectionElement =
            section.selector
              ? document.querySelector(
                  section.selector
                )
              : document.getElementById(
                  section.id
                );

          if (!sectionElement) {
            return;
          }

          const sectionHTML =
            parsedState.sections &&
            parsedState.sections[
              section.id
            ];

          if (!sectionHTML) {
            return;
          }

          sectionElement.innerHTML =
            this.getSectionInnerHTML(
              sectionHTML,
              section.selector
            );

          giftCart();

        }
      );

    setTimeout(
      () => {

        const overlay =
          this.querySelector(
            '#CartDrawer-Overlay'
          );

        if (overlay) {

          overlay.addEventListener(
            'click',
            this.close.bind(this)
          );

        }

        this.open();

      },
      100
    );
  }

  /*
   * =========================================
   * SECTION HTML
   * =========================================
   */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {

    const parsedHTML =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    const element =
      parsedHTML.querySelector(
        selector
      );

    return element
      ? element.innerHTML
      : '';
  }

  /*
   * =========================================
   * SECTIONS
   * =========================================
   */

  getSectionsToRender() {

    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  /*
   * =========================================
   * SECTION DOM
   * =========================================
   */

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {

    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(
        selector
      );
  }

  /*
   * =========================================
   * ACTIVE ELEMENT
   * =========================================
   */

  setActiveElement(
    element
  ) {

    this.activeElement =
      element;
  }
}

customElements.define(
  'cart-drawer',
  CartDrawer
);


/*
 * =============================================
 * CART DRAWER ITEMS
 * =============================================
 */

class CartDrawerItems extends CartItems {

  getSectionsToRender() {

    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];

  }
}

customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);


/*
 * =============================================
 * GIFT CART
 * =============================================
 */

function giftCart() {

  const cartItems =
    document.querySelectorAll(
      'tr.cart-item'
    );

  cartItems.forEach(
    (cartItem) => {

      const dataId =
        cartItem.getAttribute(
          'data-id'
        );

      const drawerFooter =
        document.querySelector(
          `#Gift-Wrapper[data-id="${dataId}"]`
        );

      if (!drawerFooter) {
        return;
      }

      const drawerFooterID =
        drawerFooter.getAttribute(
          'data-id'
        );

      if (
        drawerFooterID == dataId
      ) {

        drawerFooter.classList.add(
          'Gift-Wrapper__hide'
        );

      }

    }
  );
}